// 忽略 ESLint 检查规则 @typescript-eslint/no-explicit-any

import axios, { AxiosHeaders } from 'axios';
import type { AxiosRequestConfig } from 'axios';
import type { components, paths } from '@/api/types';
import Cookies from 'js-cookie';
import { handleApiError } from '@/utils/errorHandler';
import { normalizeAccessToken } from '@/utils/authToken';

type FuriFlowAxiosConfig<T = unknown> = AxiosRequestConfig<T> & {
  furiflow?: {
    /**
     * If true, a 401 from this request is treated as an expected/optional auth case.
     * We will NOT trigger global logout/redirect for that 401.
     */
    authOptional?: boolean;
  };
};

export interface AuthSuccessResponse {
  accessToken?: string | null;
  userId: string;
  [key: string]: unknown;
}

// 从 API 类型定义中提取类型
type VocabularyListDto = components['schemas']['VocabularyListDto'];
type VocabularyWord = components['schemas']['VocabularyWord'];
type AddWordDto = components['schemas']['AddWordDto'];

type VocabularyListParams = paths['/vocabulary/lists']['get']['parameters']['query'];
type VocabularyListOrderBy = NonNullable<VocabularyListParams>['orderBy'];
type OrderDirection = NonNullable<VocabularyListParams>['direction'];

// 邮箱验证相关类型
type VerifyEmailDto = components['schemas']['VerifyEmailDto'];
type VerifyEmailResponseDto = components['schemas']['VerifyEmailResponseDto'];
type ResendVerificationDto = components['schemas']['ResendVerificationDto'];
type ResendVerificationResponseDto = components['schemas']['ResendVerificationResponseDto'];

// 创建一个 axios 实例
const api = axios.create({
  baseURL: '/api',
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // 允许跨域请求携带 cookie
  timeout: 30000, // 30秒超时
});

// 添加请求拦截器
api.interceptors.request.use(
  config => {
    // 为了避免代理/跨域/浏览器策略导致 cookie 未附带，统一补充 Authorization。
    // 后端 JwtStrategy 同时支持 Authorization header 与 cookie。
    const rawToken = Cookies.get('accessToken');
    const token = normalizeAccessToken(rawToken);
    if (token) {
      // 避免覆盖调用方显式设置的 Authorization
      if (!config.headers) {
        config.headers = new AxiosHeaders();
      }

      const headers = config.headers as AxiosHeaders | Record<string, unknown>;
      const existingAuth =
        typeof (headers as AxiosHeaders).get === 'function'
          ? (headers as AxiosHeaders).get('Authorization')
          : (headers as Record<string, unknown>).Authorization;

      if (!existingAuth) {
        if (typeof (headers as AxiosHeaders).set === 'function') {
          (headers as AxiosHeaders).set('Authorization', `Bearer ${token}`);
        } else {
          (headers as Record<string, unknown>).Authorization = `Bearer ${token}`;
        }
      }
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  },
);

// 添加响应拦截器
api.interceptors.response.use(
  response => {
    // 成功响应直接返回
    return response;
  },
  error => {
    const statusCode: number | undefined = error?.response?.status;
    const cfg = (error?.config || {}) as FuriFlowAxiosConfig;
    const authOptional = Boolean(cfg.furiflow?.authOptional);
    const isOptionalAuth401 = authOptional && statusCode === 401;

    // 统一处理错误
    // - 对于“可匿名”的接口，401 不应触发全局登出/跳转，也不弹“登录过期”提示
    const apiError = handleApiError(error, undefined, !isOptionalAuth401, !isOptionalAuth401);
    return Promise.reject(apiError);
  },
);

// 注册用户
// 注意: password 必须使用 AES 加密后传递
export const registerUser = async (userData: {
  name: string;
  email: string;
  password: string; // AES 加密后的密码
}): Promise<components['schemas']['User']> => {
  const response = await api.post<components['schemas']['User']>('/users', userData);
  return response.data;
};

// 用户登录
export const loginUser = async (loginData: { email: string; password: string }) => {
  const response = await api.post<AuthSuccessResponse>('/users/login', loginData);
  return response.data;
};

// Google 登录
export const loginWithGoogle = async (googleData: { idToken: string }) => {
  const response = await api.post<AuthSuccessResponse>('/auth/google/verify', googleData);
  return response.data;
};

// 修改密码
export const changePassword = async (passwordData: { userId: string; newPassword: string }) => {
  const response = await api.post('/users/change-password', passwordData);
  return response.data;
};

// 验证邮箱
export const verifyEmail = async (token: string): Promise<VerifyEmailResponseDto> => {
  const response = await api.post<VerifyEmailResponseDto>('/auth/verify-email', {
    token,
  } satisfies VerifyEmailDto);
  return response.data;
};

// 重新发送验证邮件
export const resendVerificationEmail = async (
  email: string,
): Promise<ResendVerificationResponseDto> => {
  const response = await api.post<ResendVerificationResponseDto>('/auth/resend-verification', {
    email,
  } satisfies ResendVerificationDto);
  return response.data;
};

// 验证 token（此函数需要自定义错误处理，不显示通知）
export const validateToken = async () => {
  try {
    const response = await api.post('/auth/validate-token');
    return response.data;
  } catch (error: unknown) {
    // 仅在明确未授权时清理认证信息；网络波动/超时不应导致强制登出
    const statusCode = (error as { statusCode?: number })?.statusCode;
    if (statusCode === 401) {
      Cookies.remove('accessToken');
      Cookies.remove('userId');
    }
    throw error;
  }
};

// 获取用户信息
export const getUserInfo = async (userId: string) => {
  const response = await api.get(`/users/${userId}`);
  return response.data;
};

export const analyzeText = async (text: string) => {
  const response = await api.post('/tools/analyze', { text });
  return response.data.result;
};

// 查词
export const searchWord = async (query: string) => {
  try {
    const response = await api.post('/dictionary/search', { query }, {
      furiflow: { authOptional: true },
    } as FuriFlowAxiosConfig<{ query: string }>);
    return response.data;
  } catch (error: unknown) {
    const statusCode = (error as { statusCode?: number })?.statusCode;
    if (statusCode === 401) {
      return {
        message: 'success',
        result: { m: '', notFound: true },
      };
    }
    throw error;
  }
};

export const searchAITranslation = async (query: string, forceRefresh?: boolean) => {
  try {
    const response = await api.post(
      '/dictionary/ai-translate',
      {
        query,
        forceRefresh,
      },
      { furiflow: { authOptional: true } } as FuriFlowAxiosConfig<{
        query: string;
        forceRefresh: boolean | undefined;
      }>,
    );
    return response.data;
  } catch (error: unknown) {
    const statusCode = (error as { statusCode?: number })?.statusCode;
    if (statusCode === 401) {
      return {
        message: 'success',
        result: { m: '', notFound: true },
      };
    }
    throw error;
  }
};

// 获取搜索历史（个人）
export const getSearchHistory = async (
  type: 'all' | 'ai' | 'dictionary' = 'all',
  limit = 20,
  userId?: string,
) => {
  try {
    const response = await api.post('/dictionary/search-history', { type, limit, userId }, {
      furiflow: { authOptional: true },
    } as FuriFlowAxiosConfig<{
      type: 'all' | 'ai' | 'dictionary';
      limit: number;
      userId?: string;
    }>);
    return response.data;
  } catch (error: unknown) {
    // 未登录时，搜索历史允许为空（避免因为可匿名接口 401 触发全局登出）
    const statusCode = (error as { statusCode?: number })?.statusCode;
    if (statusCode === 401) {
      return { result: { items: [] } };
    }
    throw error;
  }
};

// 获取词典trending（全站统计）
export const getDictionaryTrending = async (
  type: 'all' | 'trending' | 'recent' = 'all',
  limit = 20,
  timeRange?: number,
) => {
  try {
    const response = await api.post('/dictionary/trending', { type, limit, timeRange }, {
      furiflow: { authOptional: true },
    } as FuriFlowAxiosConfig<{
      type: 'all' | 'trending' | 'recent';
      limit: number;
      timeRange?: number;
    }>);
    return response.data;
  } catch (error: unknown) {
    console.error('获取词典trending失败:', error);
    return { result: { items: [] } };
  }
};

// 获取AI翻译trending（全站统计）
export const getAITranslationTrending = async (
  type: 'all' | 'trending' | 'recent' = 'all',
  limit = 20,
  timeRange?: number,
) => {
  try {
    const response = await api.post('/dictionary/ai-translation/trending', { type, limit, timeRange }, {
      furiflow: { authOptional: true },
    } as FuriFlowAxiosConfig<{
      type: 'all' | 'trending' | 'recent';
      limit: number;
      timeRange?: number;
    }>);
    return response.data;
  } catch (error: unknown) {
    console.error('获取AI翻译trending失败:', error);
    return { result: { items: [] } };
  }
};

interface ApiResponse<T> {
  code: number;
  message: string;
  data: T;
}

interface VocabularyListsResponse {
  lists: VocabularyListDto[];
  total: number;
}

export const getVocabularyLists = async (params: {
  page: number;
  limit: number;
  orderBy: VocabularyListOrderBy;
  direction: OrderDirection;
}): Promise<VocabularyListsResponse> => {
  try {
    const response = await api.get<ApiResponse<VocabularyListsResponse>>('/vocabulary/lists', {
      params,
    });
    return response.data.data;
  } catch (error) {
    console.error('获取生词本列表失败:', error);
    throw error;
  }
};

export const createVocabularyList = async (
  data: components['schemas']['CreateVocabularyListDto'],
): Promise<VocabularyListDto> => {
  try {
    const response = await api.post<ApiResponse<VocabularyListDto>>('/vocabulary/lists', data);
    return response.data.data;
  } catch (error) {
    console.error('创建生词本失败:', error);
    throw error;
  }
};

export const updateVocabularyListName = async (
  id: number,
  name: string,
): Promise<VocabularyListDto> => {
  const response = await api.put(`/vocabulary/lists/${id}/name`, { name });
  return response.data;
};

export const getVocabularyListWords = async (id: number): Promise<VocabularyWord[]> => {
  const response = await api.get<components['schemas']['GetListWordsResponseDto']>(
    `/vocabulary/lists/${id}/words`,
  );
  return response.data.data.words || [];
};

export const addWordToList = async (
  listId: number,
  wordData: AddWordDto,
): Promise<VocabularyWord> => {
  const response = await api.post(`/vocabulary/lists/${listId}/words`, wordData);
  return response.data;
};

export const updateWordNote = async (wordId: number, note: string): Promise<VocabularyWord> => {
  const response = await api.put(`/vocabulary/words/${wordId}/note`, { note });
  return response.data;
};

export const removeWordFromList = async (
  listId: number,
  wordId: number,
): Promise<{ success: boolean }> => {
  const response = await api.delete(`/vocabulary/lists/${listId}/words/${wordId}`);
  return response.data;
};

export const deleteVocabularyList = async (id: number): Promise<{ success: boolean }> => {
  const response = await api.delete(`/vocabulary/lists/${id}`);
  return response.data;
};

export const deleteVocabularyListWord = async (listId: number, wordId: number) => {
  const response = await api.delete(`/vocabulary/lists/${listId}/words/${wordId}`);
  return response.data;
};

export const deleteVocabularyListWords = async (listId: number, data: { wordIds: number[] }) => {
  const response = await api.delete(`/vocabulary/lists/${listId}/words`, { data });
  return response.data;
};

export const updateVocabularyList = async (
  id: number,
  data: components['schemas']['UpdateVocabularyListDto'],
): Promise<{
  code?: number;
  message?: string;
  data?: {
    id?: number;
    name?: string;
    themeColor?: string;
    isPublic?: boolean;
    tags?: string[];
    wordCount?: number;
  };
}> => {
  const response = await api.put(`/vocabulary/lists/${id}`, data);
  return response.data;
};

export const exportVocabularyList = async (id: number) => {
  const response = await api.get(`/vocabulary/lists/${id}/export`, {
    responseType: 'blob',
  });
  return response.data;
};

/**
 * 检查单词是否在用户词库中
 */
export const checkWordStatus = async (word: string) => {
  try {
    const response = await api.post<components['schemas']['CheckWordStatusResponseDto']>(
      '/vocabulary/check-word',
      { word },
      { furiflow: { authOptional: true } } as FuriFlowAxiosConfig<{ word: string }>,
    );
    return response.data;
  } catch (error: unknown) {
    // 未登录状态特殊处理
    const statusCode = (error as { statusCode?: number })?.statusCode;
    if (statusCode === 401) {
      return {
        code: 200,
        message: '成功查询单词状态',
        data: {
          isInVocabulary: false,
          wordInfo: null,
        },
      };
    }
    throw error;
  }
};

export const getWordAudio = async (word: string, lang = 'ja-JP') => {
  // 优先尝试使用 Web Speech API
  if ('speechSynthesis' in window) {
    const utterance = new SpeechSynthesisUtterance(word);
    utterance.lang = lang;
    // 为日语设置合适的语音
    if (lang === 'ja-JP') {
      // 获取所有可用的语音
      const voices = window.speechSynthesis.getVoices();
      // 尝试找到日语语音
      const japaneseVoice = voices.find(
        voice => voice.lang.includes('ja') && !voice.lang.includes('zh'),
      );
      if (japaneseVoice) {
        utterance.voice = japaneseVoice;
      }
    }
    window.speechSynthesis.speak(utterance);
    return;
  }

  // 如果浏览器不支持 Web Speech API，可以使用第三方 TTS 服务作为备选
  // 这里使用 Google Translate TTS 作为示例
  const url = `https://translate.google.com/translate_tts?ie=UTF-8&q=${encodeURIComponent(
    word,
  )}&tl=${lang}&client=tw-ob`;

  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error('Failed to fetch audio');
    const blob = await response.blob();
    const audio = new Audio(URL.createObjectURL(blob));
    await audio.play();
  } catch (error) {
    console.error('Failed to play audio:', error);
    throw error;
  }
};

// 请求重置密码
export const requestPasswordReset = async (data: { email: string }) => {
  const response = await api.post('/users/request-password-reset', data);
  return response.data;
};

// 重置密码
export const resetPassword = async (data: { token: string; password: string }) => {
  const response = await api.post('/users/reset-password', data);
  return response.data;
};
